import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarPriceDependents } from "./solar-price-dependents"
import type {
  SolarSystemPricingDto,
  UpdateSolarProposalMarginDto,
} from "../dto/solar-proposal-system-pricing.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type UpdateSolarProposalMarginVariables = {
  proposalId: string
  body: UpdateSolarProposalMarginDto
}

/**
 * Saves the $/W margin, then refreshes pricing, both inside `mutationFn` so
 * the refresh still runs if the drawer unmounts mid-save (React Query drops
 * `mutate()` callbacks). A failed refresh leaves the saved margin: the step
 * and the investment section read again and say out of date.
 */
export default function useUpdateSolarProposalMargin(): UseMutationResult<
  SolarSystemPricingDto,
  unknown,
  UpdateSolarProposalMarginVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      proposalId,
      body,
    }: UpdateSolarProposalMarginVariables) => {
      const saved = await solarProposalService.putMargin(proposalId, body)
      // The saved margin reseeds the draft while pricing refreshes.
      queryClient.setQueryData(
        queryKeys.solarProposal.systemPricing(proposalId),
        saved,
      )
      try {
        const pricing = await solarProposalService.postPricing(proposalId)
        queryClient.setQueryData(
          queryKeys.solarProposal.pricing(proposalId),
          pricing,
        )
      } catch {
        // The save stands; the reads below report pricing out of date.
      }
      return saved
    },
    onSuccess: (_saved, { proposalId }) => {
      invalidateSolarPriceDependents(queryClient, proposalId)
    },
    // A lock or fixed margin that appeared changes what the step may do.
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.systemPricing(proposalId),
        exact: true,
      })
    },
  })
}
